import React from 'react'
import { computeWedgePath } from './wedgePath'
import './CircularProgress.scss'

export interface CircularProgressProps {
  /** Progress value in `[0, 1]`. Clamped at runtime; NaN treated as 0. */
  value: number
  /** Outer width/height in px. Defaults to 16 (Figma spec). */
  size?: number
  /** Ring stroke width in px. Defaults to 1.5 (Figma spec). */
  strokeWidth?: number
  /** Diameter of the inner filled wedge in px. Defaults to `size - 5` (≈ 11 at size 16). */
  wedgeSize?: number
  className?: string
  ariaLabel?: string
}

/**
 * A 16×16 progress donut: violet outer ring (always full) plus an inner pie
 * wedge whose sweep angle represents `value` (0..1). Matches Figma spec
 * `1628:241630` ("Project Progress"). Both ring and wedge inherit color via
 * `currentColor`, defaulting to `--space-700` so they re-tint per life space.
 */
export const CircularProgress: React.FC<CircularProgressProps> = ({
  value,
  size = 16,
  strokeWidth = 1.5,
  wedgeSize,
  className,
  ariaLabel,
}) => {
  const v = Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0
  const cx = size / 2
  const cy = size / 2
  const ringRadius = (size - strokeWidth) / 2
  const wedgeR = (wedgeSize ?? size - 5) / 2
  const wedgePath = computeWedgePath(v, cx, cy, wedgeR)

  return (
    <svg
      className={`circular-progress${className ? ` ${className}` : ''}`}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={ariaLabel}
    >
      <circle
        className="circular-progress__ring"
        cx={cx}
        cy={cy}
        r={ringRadius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
      />
      {wedgePath && (
        <path
          className="circular-progress__wedge"
          d={wedgePath}
          fill="currentColor"
        />
      )}
    </svg>
  )
}
